import leaflet, { LeafletEventHandlerFnMap, PathOptions } from "leaflet";
import React, { ReactNode } from "react";
import rf, { Circle, Polygon, Rectangle } from "react-leaflet";

export enum GeoZoneType {
    Circle = "circle",
    Square = "square",
    Polygon = "polygon",
    Polyline = "polyline",
}

export type Zone = CircleZone | SquareZone | PolygonZone;

export interface CircleZone {
    type: GeoZoneType.Circle;
    center: [number, number];
    radius: number;
}

export interface SquareZone {
    type: GeoZoneType.Square;
    bounds: [[number, number], [number, number]];
}

export interface PolygonZone {
    type: GeoZoneType.Polygon;
    positions: [number, number][];
}

export interface GeoZoneRecord {
    id: string;
    name: string;
    zone: Zone;
}

export function getZoneFromLeaflet(path: L.Layer): Zone {
    if (path instanceof leaflet.Circle) {
        const pos = path.getLatLng();
        const center = [pos.lat, pos.lng];
        const radius = path.getRadius();

        return {
            type: GeoZoneType.Circle,
            center: center,
            radius: radius,
        } as CircleZone;
    }

    if (path instanceof leaflet.Rectangle) {
        const boundsRaw = path.getBounds();
        const bounds: SquareZone["bounds"] = [
            [boundsRaw.getSouth(), boundsRaw.getWest()],
            [boundsRaw.getNorth(), boundsRaw.getEast()],
        ];

        return {
            type: GeoZoneType.Square,
            bounds: bounds,
        };
    }

    if (path instanceof leaflet.Polygon) {
        const latLngs = path.getLatLngs()[0] as leaflet.LatLng[];
        const positions = latLngs.map(
            (p) => [p.lat, p.lng] as [number, number]
        );

        return {
            type: GeoZoneType.Polygon,
            positions: positions,
        };
    }

    return {
        type: GeoZoneType.Polygon,
        positions: [],
    };
}

export function getLeafletFromZone(zone: Zone): L.Path {
    switch (zone.type) {
        case GeoZoneType.Circle:
            return leaflet.circle(zone.center, zone.radius);
        case GeoZoneType.Square:
            return leaflet.rectangle(zone.bounds);
        case GeoZoneType.Polygon:
            return leaflet.polygon(zone.positions);
    }
}

export interface GeoZoneProps {
    zone: Zone;
    children?: ReactNode;
    lefRef?: (path: leaflet.Path | null) => void;
    pathOptions?: PathOptions;
    eventHandlers?: LeafletEventHandlerFnMap;
}
export function ZoneComponent({ zone, children, ...props }: GeoZoneProps) {
    switch (zone.type) {
        case GeoZoneType.Circle:
            return (
                <Circle center={zone.center} radius={zone.radius} {...props}>{children}</Circle>
            );
        case GeoZoneType.Square:
            return <Rectangle bounds={zone.bounds} {...props}>{children}</Rectangle>;
        case GeoZoneType.Polygon:
            return <Polygon positions={zone.positions} {...props}>{children}</Polygon>;
    }
    return null;
}

export function GetZoneBounds(zone: Zone): leaflet.LatLngBounds {
    switch (zone.type) {
        case GeoZoneType.Circle: {
            const lat = zone.center[0];
            const lng = zone.center[1];

            const radiusLat = zone.radius / 110574;
            const radiusLng = zone.radius / (111320 * Math.cos(lat));

            const southWest = [lat - radiusLat, lng - radiusLng] as [number, number];
            const northEast = [lat + radiusLat, lng + radiusLng] as [number, number];

            const bounds = new leaflet.LatLngBounds(southWest, northEast);

            return bounds;
        }
        case GeoZoneType.Square: {
            const bounds = new leaflet.LatLngBounds(zone.bounds);

            return bounds;
        }
        case GeoZoneType.Polygon: {
            const bounds = new leaflet.LatLngBounds(zone.positions);

            return bounds;
        }

        default:
            return new leaflet.LatLngBounds(
                leaflet.latLng(-90, -180),
                leaflet.latLng(90, 180)
            );
    }
}
